import React, { useCallback } from 'react';
import { useCanvasAnimation } from './useCanvasAnimation';

export interface OrbitSimulationProps {
  size: number;
  bodyCount: number;
  starColor: string;
  planetColors: string[];
  speedMultiplier: number;
}

export function OrbitSimulation({
  size,
  bodyCount,
  starColor,
  planetColors,
  speedMultiplier,
}: OrbitSimulationProps) {
  const draw = useCallback(
    (ctx: CanvasRenderingContext2D, frameCount: number) => {
      ctx.clearRect(0, 0, size, size);
      const center = size / 2;
      const starRadius = size * 0.08;

      // Draw Star
      ctx.beginPath();
      ctx.arc(center, center, starRadius, 0, Math.PI * 2);
      ctx.fillStyle = starColor;
      ctx.shadowBlur = 15;
      ctx.shadowColor = starColor;
      ctx.fill();
      ctx.shadowBlur = 0; // reset

      // Draw Planets
      const maxOrbitRadius = (size / 2) * 0.9;
      const step = (maxOrbitRadius - starRadius * 1.5) / bodyCount;

      for (let i = 0; i < bodyCount; i++) {
        const orbitRadius = starRadius * 1.5 + step * (i + 1);
        
        // Draw Orbit Path
        ctx.beginPath();
        ctx.arc(center, center, orbitRadius, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Planet Logic
        // Slower orbit for outer planets
        const speed = (0.02 / (i + 1)) * speedMultiplier;
        const angle = frameCount * speed + (i * Math.PI) / 1.5; // Offset start angle
        const x = center + Math.cos(angle) * orbitRadius;
        const y = center + Math.sin(angle) * orbitRadius;
        const planetColor = planetColors[i % planetColors.length];
        const planetRadius = size * 0.015 + (i % 3) * size * 0.005;

        // Draw Planet
        ctx.beginPath();
        ctx.arc(x, y, planetRadius, 0, Math.PI * 2);
        ctx.fillStyle = planetColor;
        ctx.fill();
      }
    },
    [size, bodyCount, starColor, planetColors, speedMultiplier]
  );

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ width: size, height: size }} className="relative flex items-center justify-center">
      <canvas ref={canvasRef} className="block w-full h-full" />
    </div>
  );
}
